// App.js

import React, { useState, useEffect, useContext } from 'react';
import { 
  Alert, 
  Backdrop,
  Box,
  Button, 
  Card,
  CardContent,
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataContext } from './DataContext';
import { DndContext } from '@dnd-kit/core';
import Dashboard from "./components/admin/Dashboard";
// SplashScreen
import SplashScreen from "./components/SplashScreen";
// Lists
import AvailableList from './components/AvailableList';
import InServiceList from './components/InServiceList';
import UnavailableList from './components/UnavailableList';
// Dialogs
import DialogOptions from "./components/DialogOptions";
import GoalDialog from "./components/GoalDialog";
import NumpadDialog from './components/NumpadDialog';
import useSyncStatus from './hooks/useSyncStatus';
import axios from 'axios';

import { getStatus, saveStatus } from './utils/indexedDB';
import { LockOutlined } from "@mui/icons-material";
import BalloonCelebration from './components/BalloonCelebration';

const App = () => {
  // Base URL para chamadas API e credenciais básicas
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const S3_URL = process.env.REACT_APP_S3_URL;
  const API_AUTH = {
    username: process.env.REACT_APP_API_USER,
    password: process.env.REACT_APP_API_PASSWORD,
  };
  const urlParams = new URLSearchParams(window.location.search);

  const { data, setData } = useContext(DataContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGoalOpen, setIsGoalOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [dropContext, setDropContext] = useState(null); // Armazena contexto do drop para processar após confirmação
  const [dialogLabel, setDialogLabel] = useState('');
  const [dialogOptions, setDialogOptions] = useState([]); // Armazena as opções do diálogo dinâmico
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogIsFinalOption, setDialogIsFinalOption] = useState(false); // Estado para ativação do botão
  const [apiLoading, setApiLoading] = useState(true);
  const [companyUuid, setCompanyUuid] = useState(localStorage.getItem('companyUuid') || ''); // Recupera o companyUuid do storage
  const [companyUuidAux, setCompanyUuidAux] = useState(localStorage.getItem('companyUuid') || '');
  const [isPassModalOpen, setIsPassModalOpen] = useState(false);
  const { isOnline, isSyncing } = useSyncStatus();
  const [loadingSplashScreen, setLoadingSplashScreen] = useState(true);
  const [celebrationName, setCelebrationName] = useState(null);

  // Evita o zoom via teclado (Ctrl + + ou Ctrl + -)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        (event.key === "+" || event.key === "-" || event.key === "0")
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Prevenindo o zoom por gestos no touchpad
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };
  
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  useEffect(() => {

    setTimeout(() => setLoadingSplashScreen(false), 1500);

    const loadData = async () => {
      setApiLoading(true);

      try {
        // Verifica se está online para trabalhar online
        if (navigator.onLine) {
          const { data } = await axios.get(API_BASE_URL+'status', {
            headers: {
              c: companyUuid,
              Authorization: `Basic ${btoa(`${API_AUTH.username}:${API_AUTH.password}`)}`,
            },
          });

          // Salva os params no IndexedDB para uso offline
          await saveStatus({
            available: data.available,
            inService: data.inService,
            unavailable: data.unavailable,
          });

          setData((prevData) => ({
            ...prevData, // Mantém as outras chaves do objeto inalteradas
            available: data.available,
            inService: data.inService,
            unavailable: data.unavailable,
          }));

          // await saveParam('config', data);
          // setParams(data);
        } else {
          // Tenta obter os params do IndexedDB
          const storedStatus = await getStatus();
          if (storedStatus) {
            setData((prevData) => ({
              ...prevData, // Mantém as outras chaves do objeto inalteradas
              available: storedStatus.available,
              inService: storedStatus.inService,
              unavailable: storedStatus.unavailable,
            }));
            setApiLoading(false);
            return;
          } else {
            alert('Offline e sem dados locais');
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setApiLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line
  }, []);

  // Função utilitária para calcular o tempo decorrido
  const calculateElapsedTime = (startTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const elapsed = Math.floor((now - start) / 1000); // Time in seconds
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const seconds = (elapsed % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  // Consulta status dos usuários via API
  const getStartParams = async () => {
    setApiLoading(true);
    try {
      const res = await axios.get(API_BASE_URL+'param/start', {
        headers: {
          c: companyUuid,
          Authorization: `Basic ${btoa(`${API_AUTH.username}:${API_AUTH.password}`)}`,
        },
      });
      const startData = JSON.parse(res.data.value)
      
      setData((prevData) => ({
        ...prevData, // Mantém as outras chaves do objeto inalteradas
        dialogOptions: startData.dialogOptions,
      }));
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Erro ao sincronizar parametros');
    } finally {
      setApiLoading(false);
    }
  }

  // Consulta status dos usuários via API
  const getUserStatus = async (startData = null) => {
    setApiLoading(true);
    try {
      let dataStatus
      if (startData) {
        dataStatus = startData
      } else {
        const { data } = await axios.get(API_BASE_URL+'status', {
          headers: {
            c: companyUuid,
            Authorization: `Basic ${btoa(`${API_AUTH.username}:${API_AUTH.password}`)}`,
          },
        });
        dataStatus = data
      }
      setData((prevData) => ({
        ...prevData, // Mantém as outras chaves do objeto inalteradas
        available: dataStatus.available,
        inService: dataStatus.inService,
        unavailable: dataStatus.unavailable,
      }));
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Erro ao sincronizar status');
    } finally {
      setApiLoading(false);
    }
  }

  // Verifica se ?reset=true está presente na URL
  useEffect(() => {
    if (urlParams.get('reset') === 'true') {
      localStorage.removeItem('companyUuid');
      setCompanyUuid('');
    }
    if (urlParams.get('c')) {
      const uuid = urlParams.get('c');
      localStorage.setItem('companyUuid', uuid);

      // Remove o parâmetro companyUuid da URL e recarrega a página
      const url = new URL(window.location);
      url.searchParams.delete('c');
      window.history.replaceState({}, '', url);
      
      // Opcionalmente, pode-se recarregar a página
      window.location.reload();  // Força o recarregamento
    }
  // eslint-disable-next-line
  }, []);

  // Sincroniza status ao carregar
  useEffect(() => {
    if (companyUuid && companyUuid !== '') {
      getStartParams()
      // getUserStatus()
    }
  // eslint-disable-next-line
  }, []);

  // Atualiza a interface a cada segundo
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setData((prevData) => ({ ...prevData }));
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [setData]);

  // Confirma seleção no diálogo
  const handleConfirm = () => {
    if (selectedValue) {
      setIsModalOpen(false);
      setDialogIsFinalOption(false);
      dropContext?.resolve(selectedValue); // Passa o valor resolvido para o onDrop
      setSelectedValue("");
      setDropContext(null);
    }
  };

  const onDragStart = (event) => {
    const itemId = event.active.id
    function findKeyByUUID(data, uuid) {
      // Verifica em 'available'
      if (data.available.some(item => item.uuid === uuid)) {
        return "available";
      }
      // Verifica em 'inService'
      if (data.inService.some(item => item.uuid === uuid)) {
        return "inService";
      }
      // Verifica em 'unavailable' (opcional, já que está vazio no exemplo)
      if (data.unavailable.some(item => item.uuid === uuid)) {
        return "unavailable";
      }
      // Retorna null ou uma mensagem se não encontrar
      return null; // ou throw new Error("UUID não encontrado");
    }
    // Adiciona a classe 'dragging' a todos os outros cards, exceto o card arrastado
    document.querySelectorAll('.card').forEach(card => {
      if (card.id && card.id === itemId) {
      } else {
        card.classList.add('dragging')
      }
    })
    let fromList = findKeyByUUID(data, itemId)
    // Adiciona a classe "dragging" a todos os itens exceto as áreas droppables
    document.querySelectorAll('.draggable').forEach(item => {
      item.classList.add('dragging')
    })
    document.querySelectorAll('.droppable').forEach(area => {
      switch (fromList) {
        case 'available':
          if(area.classList.contains('inService') || area.classList.contains('unavailable')) {
            area.classList.add('highlight')
          }
          break;
        case 'inService':
          if(area.classList.contains('available')) {
            area.classList.add('highlight')
          } else if(area.classList.contains('unavailable')) {
            area.classList.add('disabled')
          }
          break;
        case 'unavailable':
          if(area.classList.contains('available')) {
            area.classList.add('highlight')
          } else if(area.classList.contains('inService')) {
            area.classList.add('disabled')
          }
          break;
      
        default:
          break;
      }
    })
  }

  const onDragEnd = () => {
    // Remove a classe "dragging" de todos os itens
    document.querySelectorAll('.dragging').forEach(item => {
      item.classList.remove('dragging');
    });
    // Remove a classe "dragging" de todos os itens
    document.querySelectorAll('.draggable').forEach(item => {
      item.classList.remove('dragging');
    });
    // Remove o destaque das áreas droppables
    document.querySelectorAll('.droppable').forEach(area => {
      area.classList.remove('highlight');
      area.classList.remove('disabled');
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setDialogIsFinalOption(false);
    dropContext?.reject();
    setDropContext(null);
    onDragEnd();
  };

  const handleCompanySubmit = () => {
    if (companyUuidAux.trim() !== '') {
      localStorage.setItem('companyUuid', companyUuidAux); // Salva no localStorage
      setCompanyUuid(companyUuidAux); // Atualiza o estado para refletir o valor
    }
  };

  const handlePinSubmit = (enteredPin) => {
    setIsPassModalOpen(false);
    dropContext?.resolve(enteredPin); // Passa o valor resolvido para o onDrop
    setDropContext(null);
    onDragEnd();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return; // Se não há destino, sai da função
    const sourceCategory = active.data.current?.sourceCategory
    const targetCategory = over.id
    const itemId = event.active.id;
    // const sourceCategory = event.dataTransfer.getData("sourceCategory");
    if (sourceCategory === targetCategory) {
      onDragEnd()
      return;
    }
    if (sourceCategory === 'unavailable' && targetCategory === 'inService') {
      setErrorMessage('Você precisa ir para a fila antes');
      return;
    }
    const sourceItems = Array.from(data[sourceCategory]);
    const [movedItem] = sourceItems.filter((item) => item.uuid === itemId);
    const dialogKey = `${sourceCategory}_${targetCategory}`;
    const option = data.dialogOptions[dialogKey] || [];
    let pin = null
    let reason = null
    if (sourceItems.length > 0 && sourceItems[0].uuid === movedItem.uuid && sourceCategory === 'available' && targetCategory === 'inService') {
    } else {
      if (option && option.options && option.options.length > 0) {
        setSelectedItem(movedItem);
        setDialogLabel(option.label);
        setDialogOptions(option.options);
        setIsModalOpen(true);
        try {
          reason = await new Promise((resolve, reject) => setDropContext({ resolve, reject }));
        } catch {
          return; // No-op: Promise rejeitada
        }
      }
    }
    if (sourceCategory === "unavailable" && targetCategory === "available") {
      setIsPassModalOpen(true)
      setSelectedItem(movedItem);
      pin = await new Promise((resolve, reject) => setDropContext({ resolve, reject }));
    }
    if (targetCategory === "inService") {
      movedItem.startedAt = new Date().toISOString();
    } else {
      delete movedItem.startedAt;
    }
    let requestData = {
      user_uuid: movedItem.uuid,
      from_status: sourceCategory,
      to_status: targetCategory,
      status_at: new Date().toISOString().slice(0, 19),
    }
    if (reason) {
      requestData.reason = reason
    }
    if (sourceCategory === "unavailable" && targetCategory === "available") {
      requestData.password = pin
    }
    setApiLoading(true);
    try {
      if (navigator.onLine) {
        const {data} = await axios.post(API_BASE_URL+'status/change', requestData, {
          headers: {
            c: companyUuid,
            Authorization: `Basic ${btoa(`${API_AUTH.username}:${API_AUTH.password}`)}`,
          },
        });
        await getUserStatus(data)
      }
      // else {
      //   await addPendingUpdate(requestData);
      // }
      if (sourceCategory === "inService" && targetCategory === "available" && requestData.reason === 'sucesso') {
        setCelebrationName(movedItem.name);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setApiLoading(false);
    }
    onDragEnd()
  }

  if (window.location.pathname === '/admin') {
    return <Dashboard />
  }

  if (loadingSplashScreen)
  return (
    <div className="app">
      <SplashScreen onFinish={() => setLoadingSplashScreen(false)} />
    </div>
  );

  if (!companyUuid) {
    
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f5f5f5">
      <Card sx={{ width: 360, p: 2, boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" textAlign="center" fontWeight="bold">
            Insira o Código de Acesso
          </Typography>

          <TextField
            label="Código de Acesso"
            variant="outlined"
            value={companyUuidAux}
            onChange={(e) => setCompanyUuidAux(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined color="primary" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            onClick={handleCompanySubmit}
            color="primary"
            variant="contained"
            fullWidth
            disabled={!companyUuidAux.trim()}
            sx={{
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: "bold",
              py: 1.2,
              transition: "all 0.3s",
              ":hover": { transform: "scale(1.05)" },
            }}
          >
            Confirmar
          </Button>
        </CardContent>
      </Card>
    </Box>
    );
  }

  return (
    <DndContext 
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
    >
      {apiLoading && 
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: "flex",
            flexDirection: "column",
            gap: 2, // Espaçamento entre o spinner e o texto
          }}
          open={apiLoading}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6">Atualizando...</Typography>
        </Backdrop>
      }
      <div className='networkStatus'>
        <span>{isOnline ? '🟢 Online' : '🔴 Offline'}</span>
        {isSyncing && <p>🔄 Sincronizando...</p>}
      </div>
      <div className="container">
        <GoalDialog 
          open={isGoalOpen}
          selectedItem={selectedItem}
          onClose={() => { setIsGoalOpen(false); }}
          setApiLoading={setApiLoading}
        />
        <NumpadDialog
          open={isPassModalOpen}
          onClose={() => { onDragEnd(); setIsPassModalOpen(false); }}
          onSubmit={handlePinSubmit}
          selectedItem={selectedItem}
        />
        <Dialog open={isModalOpen} onClose={handleCancel}>
          { selectedItem && (
            <div
            className="card"
            >
              <div className="avatar">
              {selectedItem.avatar_url && selectedItem.avatar_url !== '' ? (
                <img src={`${S3_URL}/img/user/${selectedItem.avatar_url}`} alt={selectedItem.name} className="avatar-img" />
              ) : (
                <div className="avatar-unavailable">👤</div>
              )}
              </div>
              <div className="info">
              <span>{selectedItem.name}</span>
              {selectedItem.startedAt && (
                <span className="timer">{calculateElapsedTime(selectedItem.status_at)}</span>
              )}
              </div>
            </div>
          )}
          <DialogTitle>{dialogLabel}</DialogTitle>
          <DialogContent>
            <DialogOptions
              options={dialogOptions} // Passa as opções dinâmicas para o componente
              selectedValue={selectedValue}
              onChange={setSelectedValue}
              dialogIsFinalOption={dialogIsFinalOption}
              setDialogIsFinalOption={setDialogIsFinalOption}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button onClick={handleConfirm} color="primary" variant="outlined" disabled={!dialogIsFinalOption}>
              Concluir
            </Button>
          </DialogActions>
        </Dialog>

        <DroppableContainer onDragStart={onDragStart}>
          <AvailableList 
            onDragStart={onDragStart} 
            onGetGoal={(item) => { setSelectedItem(item); setIsGoalOpen(true); }}
          />
        </DroppableContainer>

        <DroppableContainer>
          <InServiceList />
        </DroppableContainer>

        <DroppableContainer>
          <UnavailableList 
            onGetGoal={(item) => { setSelectedItem(item); setIsGoalOpen(true); }}
          />
        </DroppableContainer>
        
        <Snackbar open={errorMessage!==''} autoHideDuration={6000} onClose={() => {setErrorMessage('')}} >
          <Alert
            onClose={() => {setErrorMessage('')}}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>

        <BalloonCelebration 
          celebrationName={celebrationName}
          setCelebrationName={setCelebrationName}
        />

      </div>
    </DndContext>
  );
};

const DroppableContainer = ({ id, children }) => {
  return (
    <div id={id}>
      {children}
    </div>
  );
};

// function delay(ms) {
//   return new Promise(resolve => setTimeout(resolve, ms));
// }

export default App;
